import React, { useContext } from 'react';
import {
  Button,
  Image,
  Text,
  Title3,
} from '@fluentui/react-components';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useAcceptInvitationStyles } from '../styles';
import HeaderLogo from '../../../media/microsoft-advertising-logo-idp-header.png';

// Which same-provider ActivateWithAlternate sub-case to render:
//   'canMerge' — the user proved ownership of the matched account by signing in (the PuidMatch
//                non-login "user name deactivated" case): show the matched account and offer to
//                continue as it.
//   'blocked'  — an AliasMatch where ownership is NOT proven: a dead-end with only the
//                "use a different email" escape hatch, and the matched account is NOT revealed.
export type AlternateAccountVariant = 'canMerge' | 'blocked';

export type ActivationAlternateAccountRequiredProps = {
  variant: AlternateAccountVariant;
  // The account the user is currently signed in with — shown only in the 'canMerge' copy. Optional.
  currentUsername?: string | null;
  // The matched account of the SAME provider the user should continue as — shown only in the
  // 'canMerge' copy (never revealed for 'blocked', where ownership isn't proven). Optional.
  expectedUsername?: string | null;
  // "Continue" — sign the current session out and re-authenticate as the matched account
  // (`expectedUsername` / same provider), then return to the accept-invitation flow. Only used by
  // the 'canMerge' variant. Resolved by the caller.
  onContinue?: () => void;
  // "Use a different email address" — sign the current session out and return to the invitation
  // entry so the user can accept with any other account. Resolved by the caller.
  onUseDifferentEmail: () => void;
};

// Shown when ActivationOptions reports `ActivateWithAlternate` for a matched account of the SAME
// identity provider as the one the user signed in with (`currentIdentityType === expectedIdentityType`).
// The cross-provider cases have dedicated "sign in with your <provider> account" pages; this covers
// the same-provider gap, keyed on `reason` by the caller into one of two variants (see above).
export const ActivationAlternateAccountRequired: React.FC<ActivationAlternateAccountRequiredProps> = ({
  variant,
  currentUsername,
  expectedUsername,
  onContinue,
  onUseDifferentEmail,
}) => {
  const i18n = useContext(I18nContext) as any;
  const classes = useAcceptInvitationStyles();

  const isCanMerge = variant === 'canMerge';

  const title = isCanMerge
    ? i18n.getString(_TL_('This user name is deactivated'))
    : i18n.getString(_TL_('This account can\'t be used'));

  const body = isCanMerge
    ? i18n.getString(
      _TL_('The user name {{currentUsername}} has been deactivated and can no longer be used to sign in to Microsoft Advertising. You can click Continue to accept this invitation to Microsoft Advertising using {{expectedUsername}}, or use a different email address instead.'),
      { currentUsername, expectedUsername }
    )
    : i18n.getString(_TL_('This account can\'t be used to accept this invitation. Please use a different email address.'));

  return (
    <div className={classes.centeredPage}>
      <div
        className={classes.errorCard}
        role="region"
        aria-labelledby="activation-alternate-required-title"
        aria-describedby="activation-alternate-required-description"
      >
        <Image className={classes.brandLogo} src={HeaderLogo} alt={i18n.getString(_TL_('Microsoft Advertising'))} />

        <Title3 id="activation-alternate-required-title" className={classes.errorTitle}>
          {title}
        </Title3>

        <div id="activation-alternate-required-description" className={classes.errorBody}>
          <Text block>{body}</Text>
        </div>

        <div className={classes.errorActions}>
          {isCanMerge && (
            <Button
              className="alternate-account-continue"
              appearance="primary"
              onClick={onContinue}
            >
              {i18n.getString(_TL_('Continue'))}
            </Button>
          )}
          <Button
            className="alternate-account-use-different-email"
            appearance={isCanMerge ? 'secondary' : 'primary'}
            onClick={onUseDifferentEmail}
          >
            {i18n.getString(_TL_('Use a different email address'))}
          </Button>
        </div>
      </div>
    </div>
  );
};
